<template>
  <div>
    <opq-alert
      type="warning"
      code="service-reference-interdiction-discipline"
      v-if="sas.ficheDisciplineOuverte"
    ></opq-alert>
    <opq-alert type="warning" code="service-reference-aucun-emploi" v-else-if="!hasEmployeursAdmissibles"></opq-alert>

    <template v-if="hasEmployeursAdmissibles && !sas.ficheDisciplineOuverte && !isLoading">
      <data-iterator
        :list="emploisReferencesAdmissibles"
        add-new-text=""
        :icon="$getIcon('job')"
        card-width="700"
        optional
        :valids="valids"
        ref="dataIterator"
        hideDots
        hideAdd
        hideDelete
        :opq-if="emploisReferencesAdmissibles.length > 0"
      >
        <template v-slot:show-mode="{ item }">
          <div class="d-flex align-center">
            <opq-case
              no-label
              v-model="item.sasEmploiReference.serviceDeReferenceActif"
              :live="liveSasEmploiReference(item, 25208)"
              optional
              :ref="'serviceDeReference-' + item.id"
              @eudo-updated="serviceReferenceCoche(item)"
            ></opq-case>
            <div class="ml-3">
              <h3>{{ item.raisonSociale }}</h3>
              <p class="my-0">{{ item.adresse }}</p>
              <p class="my-0">{{ item.ville }}</p>
            </div>
          </div>
        </template>
        <template v-slot:edit-mode="{ item }">
          <template v-if="item.sasEmploiReference && item.sasEmploiReference.serviceDeReferenceActif">
            <div class="mt-4">
              <ressource code="information-services-offerts"></ressource>
            </div>
            <div>
              <ressource code="information-modalite-intervention"></ressource>
            </div>
            <opq-catalogue
              code="service-reference-modalite-intervention"
              v-model="item.sasEmploiReference.modalitesDIntervention"
              :live="liveSasEmploiReference(item, 25203)"
              optional
              order-by="text"
            ></opq-catalogue>
            <opq-text-field
              code="service-reference-modalite-autre"
              v-model="item.sasEmploiReference.autresModalitesDIntervention"
              :live="liveSasEmploiReference(item, 25204)"
              optional
            ></opq-text-field>
            <div>
              <ressource code="service-reference-disponibilite"></ressource>
            </div>
            <opq-catalogue
              code="service-reference-disponibilite-emploi"
              v-model="item.sasEmploiReference.disponibilitePourRecevoirDeNouveauxClients"
              :live="liveSasEmploiReference(item, 25206)"
            ></opq-catalogue>
            <opq-catalogue
              code="service-reference-clients"
              v-model="item.sasEmploiReference.recevoirDeNouveauxClients"
              :live="liveSasEmploiReference(item, 25207)"
            ></opq-catalogue>
          </template>
          <template v-else>
            <p class="grey--text">
              <ressource code="emploi-reference-ouvert"></ressource>
            </p>
          </template>
        </template>
      </data-iterator>
    </template>
  </div>
</template>

<script>
import { Api, Queue } from "@/plugins/api"
import CataloguesHelper from "@/mixins/CataloguesHelpers"
import { bus } from "@/plugins/bus"

export default {
  mixins: [CataloguesHelper],
  props: {
    sas: { type: Object, required: true },
    valids: { type: Array, default: () => [] }
  },
  data() {
    return {
      localEmploisReferences: [],
      isLoading: true,
    }
  },
  async created() {
    // Initialiser les sas emplois références pour chaque carte d'affaire privée à l'ouverture
    await this.initEmploisReferences()
    this.isLoading = false

    // Écouter les événements de création/suppression de carte d'affaire
    bus.$on("sasCarteAffaireCreated", this.onCarteAffaireCreated)
    bus.$on("sasCarteAffaireDeleted", this.onCarteAffaireDeleted)
    bus.$on("sasCarteAffaireTypeChanged", this.onCarteAffaireTypeChanged)
    bus.$on("decocheServiceReference", this.decocheServiceReference)
  },
  beforeDestroy() {
    bus.$off("sasCarteAffaireCreated", this.onCarteAffaireCreated)
    bus.$off("sasCarteAffaireDeleted", this.onCarteAffaireDeleted)
    bus.$off("sasCarteAffaireTypeChanged", this.onCarteAffaireTypeChanged)
    bus.$off("decocheServiceReference", this.decocheServiceReference)
  },
  methods: {
    liveSasCartesAffaires(item, cle) {
      return { endpoint: "sas-cartes-affaires", cle: cle, fileId: item.id }
    },
    liveSasEmploiReference(item, cle) {
      // S'assurer que l'employeur a une fiche SasEmploiReference
      if (!item.sasEmploiReference) {
        item.sasEmploiReference = { sasCartesDAffaires: item.id, serviceDeReferenceActif: false }
      }
      // Ne retourner le live que si l'id existe, sinon null (pas de sauvegarde auto)
      if (!item.sasEmploiReference.id) {
        return null
      }
      return { endpoint: "sas-emplois-references", cle: cle, fileId: item.sasEmploiReference.id, parentId: this.sas.id }
    },

    estTypeEmploi(item, type) {
      return this.getCatalogueCodeById(2902, item.typeDEmploi) == type
    },

    /**
     * Initialise les sas emplois références pour chaque carte d'affaire pratique privée
     * Appel API pour créer les sas manquants côté backend
     */
    async initEmploisReferences() {
      try {
        const response = await Api.post("sas-emplois-references/init/" + this.sas.id)
        if (response.data) {
          // Mettre à jour les employeurs avec les sasEmploiReference créés
          this.syncLocalEmploisReferences(response.data)
          this.syncEmploisReferencesWithEmployeurs(response.data)
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation des emplois références:", error)
      }
    },

    /**
     * Synchronise les emplois références retournés par l'API avec les emplois références locaux
     */
    syncLocalEmploisReferences(emploisReferences) {
      for (const emploiRef of emploisReferences) {
        const localEmploiRef = this.localEmploisReferences.find((e) => e.id === emploiRef.id)
        if (!localEmploiRef) {
          this.localEmploisReferences.push(emploiRef)
        }
      }
    },

    /**
     * Synchronise les emplois références retournés par l'API avec les employeurs
     */
    syncEmploisReferencesWithEmployeurs(emploisReferences) {
      for (const emploiRef of emploisReferences) {
        const employeur = this.sas.employeurs.find((e) => e.id === emploiRef.sasCartesDAffaires)
        if (employeur) {
          this.$set(employeur, "sasEmploiReference", emploiRef)
        }
      }
    },

    /**
     * Gère le cochage/décochage du service de référence
     * Le sasEmploiReference existe déjà (créé à l'init), on ne fait que forcer le rafraîchissement
     */
    async serviceReferenceCoche(sasCarteDAffaire) {
      // Forcer le rafraîchissement de la vue pour afficher/masquer les champs
      this.$forceUpdate()
      // Émettre un événement pour informer le parent
      this.$emit("service-reference-changed", sasCarteDAffaire)
    },

    /**
     * Décoche le service de référence pour un employeur
     */
    decocheServiceReference(id) {
      let ref = this.$refs["serviceDeReference-" + id]
      if (ref && ref[0]) {
        ref[0].setValue(false)
      }
    },

    /**
     * Appelé quand une nouvelle carte d'affaire est créée
     */
    async onCarteAffaireCreated(carteAffaire) {
      // Vérifier si c'est une carte d'affaire de type privé
      const isPri = this.estTypeEmploi(carteAffaire, "Pri")
      if (isPri && !carteAffaire.inactif) {
        // Créer automatiquement le sas emploi référence associé
        try {
          const response = await Api.post("sas-emplois-references/new/" + this.sas.id, {
            sasCartesDAffaires: carteAffaire.id,
          })
          if (response.data) {
            this.$set(carteAffaire, "sasEmploiReference", response.data)
            this.localEmploisReferences.push(response.data)
          }
        } catch (error) {
          console.error("Erreur lors de la création du sas emploi référence:", error)
        }
      }
    },

    /**
     * Appelé quand une carte d'affaire est supprimée
     */
    async onCarteAffaireDeleted(carteAffaireId) {
      // Trouver et supprimer le sas emploi référence associé
      const localEmploiRef = this.localEmploisReferences.find((e) => e.sasCartesDAffaires === carteAffaireId)
      if (localEmploiRef && localEmploiRef.id) {
        try {
          await Api.post("sas-emplois-references/delete/" + localEmploiRef.id)
          this.localEmploisReferences.splice(this.localEmploisReferences.indexOf(localEmploiRef), 1)
        } catch (error) {
          console.error("Erreur lors de la suppression du sas emploi référence:", error)
        }
      }
    },

    /**
     * Appelé quand le type d'emploi d'une carte d'affaire change
     */
    async onCarteAffaireTypeChanged(payload) {
      const { carteAffaire, oldType, newType } = payload
      const isPri = newType === "Pri"
      const wasPri = oldType === "Pri"

      if (wasPri && !isPri) {
        // Passage de privé à public/salarié : supprimer le sas emploi référence
        if (carteAffaire.sasEmploiReference && carteAffaire.sasEmploiReference.id) {
          try {
            await Api.post("sas-emplois-references/delete/" + carteAffaire.sasEmploiReference.id)
            this.$delete(carteAffaire, "sasEmploiReference")

            const localEmploiRef = this.localEmploisReferences.find((e) => e.sasCartesDAffaires === carteAffaire.id )
            if (localEmploiRef) {
              this.localEmploisReferences.splice(this.localEmploisReferences.indexOf(localEmploiRef), 1)
            }
          } catch (error) {
            console.error("Erreur lors de la suppression du sas emploi référence:", error)
          }
        }
      } else if (!wasPri && isPri) {
        // Passage de public/salarié à privé : créer le sas emploi référence
        if (!carteAffaire.inactif) {
          try {
            const response = await Api.post("sas-emplois-references/new/" + this.sas.id, {
              sasCartesDAffaires: carteAffaire.id,
            })
            if (response.data) {
              this.$set(carteAffaire, "sasEmploiReference", response.data)
              this.localEmploisReferences.push(response.data)
            }
          } catch (error) {
            console.error("Erreur lors de la création du sas emploi référence:", error)
          }
        }
      }
    },
  },
  computed: {
    /**
     * Retourne les employeurs admissibles pour le service de référence
     * (type privé, non inactif et non suspendu temporairement)
     */
    employeursAdmissibles() {
      return (
        this.sas?.employeurs?.filter((e) => this.estTypeEmploi(e, "Pri") && !e.inactif && !e.suspenduTemporairement) ??
        []
      )
    },
    hasEmployeursAdmissibles() {
      return this.employeursAdmissibles.length > 0
    },
    /**
     * Liste des emplois références admissibles à afficher dans le data-iterator
     */
    emploisReferencesAdmissibles() {
      return this.employeursAdmissibles
    },
    employeursInscrits() {
      return this.employeursAdmissibles.filter((e) => e.sasEmploiReference?.serviceDeReferenceActif)
    },
    hasEmployeursInscrits() {
      return this.employeursInscrits.length > 0
    },
  },
}
</script>

<style scoped></style>
